import React, { useEffect, useState } from 'react'
import { RotateCcw, RefreshCw, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react'
import { jobsDashboard, jobsFailedList, jobsRetryFailure, jobsRetryRun } from '../services/api'

type Job = { name: string; title: string; last_run_at?: string; next_run_eta?: string }
type Run = {
  name: string; status: 'RUNNING'|'SUCCESS'|'ERROR';
  started_at: string; finished_at?: string;
  processed_count: number; succeeded_count: number; skipped_count: number; failed_count: number;
  summary_json?: string;
}
type Failure = {
  name: string; failure_type: string; driver_user?: string; driver_profile?: string;
  iccid?: string; email?: string; error_message: string; ts: string; retry_status: string
}

export const BackgroundJobsDashboard: React.FC = () => {
  const [job, setJob] = useState<Job | null>(null)
  const [run, setRun] = useState<Run | null>(null)
  const [fails, setFails] = useState<Failure[]>([])
  const [totalFails, setTotalFails] = useState(0)
  const [loading, setLoading] = useState(true)
  const [retrying, setRetrying] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    try {
      const d = await jobsDashboard('drivers-auto-renew')
      setJob(d.job)
      setRun(d.run)
      if (d.run) {
        const f = await jobsFailedList('drivers-auto-renew', 1, 25)
        setFails(f.items)
        setTotalFails(f.total)
      } else {
        setFails([])
        setTotalFails(0)
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  async function retryOne(name: string) {
    setRetrying(name)
    try {
      await jobsRetryFailure(name)
      await load()
    } catch (error) {
      console.error('Failed to retry:', error)
      alert('Failed to retry. Please try again.')
    } finally {
      setRetrying(null)
    }
  }

  async function retryAll() {
    setRetrying('all')
    try {
      await jobsRetryRun('drivers-auto-renew')
      await load()
    } catch (error) {
      console.error('Failed to retry all:', error)
      alert('Failed to retry all. Please try again.')
    } finally {
      setRetrying(null)
    }
  }

  useEffect(() => { load() }, [])

  if (loading) return (
    <div className="p-6">
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600">Loading dashboard...</span>
      </div>
    </div>
  )

  const processed = run?.processed_count ?? 0
  const succeeded = run?.succeeded_count ?? 0
  const skipped = run?.skipped_count ?? 0
  const failed = run?.failed_count ?? 0

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'RUNNING': return <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />
      case 'SUCCESS': return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'ERROR': return <XCircle className="h-4 w-4 text-red-500" />
      default: return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getRetryStatusBadge = (status: string) => {
    const classes = {
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'RETRIED': 'bg-blue-100 text-blue-800',
      'SUCCEEDED': 'bg-green-100 text-green-800',
      'IGNORED': 'bg-gray-100 text-gray-800'
    }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classes[status as keyof typeof classes] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Background Jobs Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">Monitor and manage automated background jobs</p>
        </div>
        <button 
          onClick={load}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Job Status Card */}
      <div className="rounded-xl border p-5 bg-white shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl font-semibold flex items-center">
              {getStatusIcon(run?.status || '')}
              <span className="ml-2">{job?.title || 'Daily Auto-Renew Job'}</span>
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Last Run: {job?.last_run_at ? new Date(job.last_run_at).toLocaleString() : '—'}
            </div>
            <div className="text-sm text-gray-500">
              Next Run (ETA): {job?.next_run_eta ? new Date(job.next_run_eta).toLocaleString() : '—'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-6">
          <StatCard label="Processed" value={processed} />
          <StatCard label="Succeeded" value={succeeded} accent="ring-emerald-500" />
          <StatCard label="Skipped" value={skipped} accent="ring-amber-500" />
          <StatCard label="Failed" value={failed} accent="ring-rose-500" />
        </div>
      </div>

      {/* Failed Jobs Table */}
      <div className="rounded-xl border bg-white shadow-sm">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div className="font-semibold">Failed Jobs</div>
          {failed > 0 && (
            <button
              onClick={retryAll}
              disabled={retrying === 'all'}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {retrying === 'all' ? (
                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <RotateCcw className="h-3 w-3 mr-1" />
              )}
              Retry All
            </button>
          )}
        </div>
        <div className="px-5 py-2 text-sm text-gray-500">{totalFails} total failures</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left bg-gray-50">
                <th className="px-5 py-3">Failure Type</th>
                <th className="px-5 py-3">User</th>
                <th className="px-5 py-3">Time</th>
                <th className="px-5 py-3">Error</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fails.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-500">
                    No failed jobs found
                  </td>
                </tr>
              ) : (
                fails.map(row => (
                  <tr key={row.name} className="border-t hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center">
                        <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                        {row.failure_type}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium">{row.driver_user || '—'}</span>
                        <span className="text-gray-500 text-xs">{row.email || ''}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {new Date(row.ts).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 max-w-xs truncate" title={row.error_message}>
                      {row.error_message}
                    </td>
                    <td className="px-5 py-3">
                      {getRetryStatusBadge(row.retry_status)}
                    </td>
                    <td className="px-5 py-3 text-right space-x-3">
                      <button 
                        onClick={() => retryOne(row.name)}
                        disabled={retrying === row.name}
                        className="text-rose-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {retrying === row.name ? 'Retrying...' : 'Retry Now'}
                      </button>
                      {row.driver_user && (
                        <a 
                          href={`/driver-users?user=${row.driver_user}`} 
                          className="text-gray-600 hover:underline"
                        >
                          View User
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function StatCard({label, value, accent}: {label: string; value: number; accent?: string}) {
  return (
    <div className={`rounded-xl border p-5 ${accent ? `ring-1 ${accent}` : ''}`}>
      <div className="text-gray-500 text-sm">{label}</div>
      <div className="text-3xl font-semibold mt-2">{value}</div>
    </div>
  )
}
